// Requêtes des écrans des achats (lot L4-C, TanStack Query), clés sous « achats ». Les types viennent
// de packages/contrats/src/achats.ts ; les besoins viennent de la chaîne de la dépense (depense.ts).
import type {
  AnalyseOffre,
  Attribution,
  BesoinDepense,
  CommandeAchat,
  Consultation,
  ContratAchat,
  CreationAvenant,
  CreationCommande,
  CreationConsultation,
  CreationContrat,
  CreationFournisseur,
  CreationLivraison,
  CreationOffre,
  CreationProcedure,
  EvaluationFournisseur,
  EvaluationFournisseurSaisie,
  FactureCommande,
  Fournisseur,
  ProcedureAchat,
  TransitionConsultation,
} from '@cc/contrats';
import { useQuery } from '@tanstack/react-query';
import { requete } from '../../api/client';

export interface Liste<T> {
  elements: T[];
}

export const INVALIDER_ACHATS: readonly (readonly unknown[])[] = [['achats'], ['depense'], ['indicateurs'], ['travaux']];

const lire = <T,>(chemin: string, parametres: Record<string, string | undefined> = {}) => ({ signal }: { signal: AbortSignal }) => requete<T>(chemin, { signal, parametres });
const poster = <T,>(chemin: string, corps: unknown) => requete<T>(chemin, { methode: 'POST', corps });

export const useFournisseurs = (recherche?: string) =>
  useQuery({ queryKey: ['achats', 'fournisseurs', recherche ?? ''], queryFn: lire<Liste<Fournisseur>>('/achats/fournisseurs', { recherche: recherche || undefined }), retry: false });
export const useFournisseur = (id: string | null) =>
  useQuery({ queryKey: ['achats', 'fournisseur', id ?? ''], queryFn: lire<Fournisseur & { historique_evaluations: EvaluationFournisseur[] }>(`/achats/fournisseurs/${id}`), enabled: Boolean(id), retry: false });
export const useProcedures = () => useQuery({ queryKey: ['achats', 'procedures'], queryFn: lire<Liste<ProcedureAchat>>('/achats/procedures'), retry: false });
export const useConsultations = () => useQuery({ queryKey: ['achats', 'consultations'], queryFn: lire<Liste<Consultation>>('/achats/consultations'), retry: false });
export const useContrats = () => useQuery({ queryKey: ['achats', 'contrats'], queryFn: lire<Liste<ContratAchat>>('/achats/contrats'), retry: false });
export const useContrat = (id: string | null) => useQuery({ queryKey: ['achats', 'contrat', id ?? ''], queryFn: lire<ContratAchat>(`/achats/contrats/${id}`), enabled: Boolean(id), retry: false });
export const useCommandes = () => useQuery({ queryKey: ['achats', 'commandes'], queryFn: lire<Liste<CommandeAchat>>('/achats/commandes'), retry: false });
/** Besoins validés de la chaîne de la dépense, origine possible d'une consultation (D-180). */
export const useBesoinsValides = () => useQuery({ queryKey: ['depense', 'besoins', 'valide'], queryFn: lire<Liste<BesoinDepense>>('/depense/besoins', { etat: 'valide' }), retry: false });

export const creerFournisseur = (corps: CreationFournisseur) => poster<Fournisseur>('/achats/fournisseurs', corps);
export const creerProcedure = (corps: CreationProcedure) => poster<ProcedureAchat>('/achats/procedures', corps);
export const creerConsultation = (corps: CreationConsultation) => poster<Consultation>('/achats/consultations', corps);
export const transiter = (id: string, corps: TransitionConsultation) => poster<Consultation>(`/achats/consultations/${id}/transitions`, corps);
export const enregistrerOffre = (id: string, corps: CreationOffre) => poster<Consultation>(`/achats/consultations/${id}/offres`, corps);
export const analyserOffre = (offreId: string, corps: AnalyseOffre) => poster<Consultation>(`/achats/offres/${offreId}/analyse`, corps);
export const attribuer = (id: string, corps: Attribution) => poster<Consultation>(`/achats/consultations/${id}/attribution`, corps);
export const creerContrat = (corps: CreationContrat) => poster<ContratAchat>('/achats/contrats', corps);
export const signerContrat = (id: string, date: string) => poster<ContratAchat>(`/achats/contrats/${id}/signature`, { date_signature: date });
export const ajouterAvenant = (id: string, corps: CreationAvenant) => poster<ContratAchat>(`/achats/contrats/${id}/avenants`, corps);
export const evaluerContrat = (id: string, corps: EvaluationFournisseurSaisie) => poster<EvaluationFournisseur>(`/achats/contrats/${id}/evaluation`, corps);
export const creerCommande = (corps: CreationCommande) => poster<CommandeAchat>('/achats/commandes', corps);
export const enregistrerLivraison = (id: string, corps: CreationLivraison) => poster<CommandeAchat>(`/achats/commandes/${id}/livraisons`, corps);
export const recevoirFacture = (id: string, corps: FactureCommande) => poster<CommandeAchat>(`/achats/commandes/${id}/factures`, corps);
